import React, { useEffect, useState } from 'react';
import { Alert, Container, Paper, Stack, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { getAuthUserId } from '../utils/authUser';
import { buildEventsApiUrl } from '../config/api';
import { isSiteAdminUser } from '../config/admin';
import { buildAuthHeaders } from '../utils/authFetch';

const formatTimestamp = (value?: string | null) => {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleString();
};

const formatMinutes = (value?: number | null) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${value.toFixed(2)} min`;
};

const SiteAdminTradeSessionMetricsPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const userId = getAuthUserId(user as any);
  const isAllowed = isSiteAdminUser(user as any);
  const [metrics, setMetrics] = useState<any[]>([]);
  const [totals, setTotals] = useState<{ completedSessionsConsidered: number; participantBuckets: number; skippedSessions: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setError(null);
        const response = await fetch(buildEventsApiUrl('/api/site-admin/trade-session-metrics'), {
          headers: buildAuthHeaders(user),
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.error || `Failed to load trade session metrics (${response.status})`);
        }
        if (cancelled) return;
        setMetrics(data.metrics || []);
        setTotals(data.totals || null);
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.message || 'Failed to load trade session metrics');
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [user]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        <Stack spacing={2.5}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Site Admin Trade Session Metrics</Typography>
          <Typography variant="body2" color="text.secondary">
            Average completed trade session duration grouped by participant count, using session start and finish timestamps.
          </Typography>

          {!isAllowed && (
            <Alert severity="warning">
              You are signed in as <strong>{userId || 'unknown'}</strong>. This area is intended for site admins.
            </Alert>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {totals && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                <Typography variant="body2"><strong>Completed sessions counted:</strong> {totals.completedSessionsConsidered}</Typography>
                <Typography variant="body2"><strong>Participant-count buckets:</strong> {totals.participantBuckets}</Typography>
                <Typography variant="body2"><strong>Skipped sessions:</strong> {totals.skippedSessions}</Typography>
              </Stack>
            </Paper>
          )}

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={1.5}>
              <Typography variant="h6">Averages by player count</Typography>
              {metrics.length > 0 ? metrics.map((entry) => (
                <Paper key={entry.participantCount} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Stack spacing={0.75}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {entry.participantCount} player{entry.participantCount === 1 ? '' : 's'}
                    </Typography>
                    <Typography variant="body2"><strong>Average duration:</strong> {formatMinutes(entry.averageDurationMinutes)}</Typography>
                    <Typography variant="body2"><strong>Sessions counted:</strong> {entry.sessionCount}</Typography>
                    <Typography variant="body2"><strong>Min / max:</strong> {formatMinutes(entry.minDurationMinutes)} / {formatMinutes(entry.maxDurationMinutes)}</Typography>
                    <Typography variant="body2"><strong>Distinct events:</strong> {entry.distinctEventCount}</Typography>
                    <Typography variant="caption" color="text.secondary"><strong>Latest completed:</strong> {formatTimestamp(entry.latestCompletedAt)}</Typography>
                  </Stack>
                </Paper>
              )) : (
                <Typography variant="body2" color="text.secondary">No completed trade session metrics yet.</Typography>
              )}
            </Stack>
          </Paper>
        </Stack>
      </Paper>
    </Container>
  );
};

export default SiteAdminTradeSessionMetricsPage;
